
import React from 'react';
import { Link } from 'react-router-dom';
import Navigation from '../../../components/feature/Navigation';
import Footer from '../../../components/feature/Footer';

const ReturnsPage: React.FC = () => {
  return (
    <div className="min-h-screen bg-white">
      <Navigation />
      
      {/* Hero Section */}
      <section className="relative py-20 px-6 bg-gradient-to-br from-green-600 to-teal-700">
        <div className="max-w-6xl mx-auto text-center">
          <h1 className="text-5xl md:text-6xl font-bold text-white mb-6">Investment Returns</h1>
          <p className="text-xl text-green-100 max-w-3xl mx-auto leading-relaxed">
            Discover the exceptional returns and consistent performance of our glamping and tiny home investments
          </p>
        </div>
      </section>

      {/* Returns Overview */}
      <section className="py-20 px-6">
        <div className="max-w-6xl mx-auto">
          <div className="grid md:grid-cols-4 gap-8 mb-16">
            <div className="text-center">
              <div className="text-4xl font-bold text-green-600 mb-2">22%</div>
              <div className="text-gray-600">Average Annual ROI</div>
            </div>
            <div className="text-center">
              <div className="text-4xl font-bold text-green-600 mb-2">15-25%</div>
              <div className="text-gray-600">ROI Range</div>
            </div>
            <div className="text-center">
              <div className="text-4xl font-bold text-green-600 mb-2">18</div>
              <div className="text-gray-600">Months Avg Payback</div>
            </div>
            <div className="text-center">
              <div className="text-4xl font-bold text-green-600 mb-2">100%</div>
              <div className="text-gray-600">Profitable Projects</div>
            </div>
          </div>
          
          <div className="text-center mb-16">
            <h2 className="text-4xl font-bold text-gray-900 mb-6">Proven Investment Performance</h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              Our track record demonstrates consistent, above-market returns across all property types and market conditions
            </p>
          </div>
        </div>
      </section>

      {/* Return Breakdown */}
      <section className="py-20 bg-gray-50 px-6">
        <div className="max-w-6xl mx-auto">
          <h2 className="text-4xl font-bold text-gray-900 text-center mb-16">Return Breakdown by Property Type</h2>
          
          <div className="grid md:grid-cols-3 gap-8">
            <div className="bg-white rounded-lg p-8 shadow-lg">
              <div className="text-center mb-6">
                <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
                  <i className="ri-mountain-line text-2xl text-green-600"></i>
                </div>
                <h3 className="text-2xl font-bold text-gray-900">Mountain Retreats</h3>
              </div>
              <div className="space-y-4">
                <div className="flex justify-between">
                  <span className="text-gray-700">Average ROI</span>
                  <span className="font-bold text-green-600">24%</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-700">Occupancy Rate</span>
                  <span className="font-bold">96%</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-700">Avg Nightly Rate</span>
                  <span className="font-bold">$285</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-700">Payback Period</span>
                  <span className="font-bold">16 months</span>
                </div>
              </div>
            </div>
            
            <div className="bg-white rounded-lg p-8 shadow-lg">
              <div className="text-center mb-6">
                <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
                  <i className="ri-water-line text-2xl text-green-600"></i>
                </div>
                <h3 className="text-2xl font-bold text-gray-900">Lakeside Properties</h3>
              </div>
              <div className="space-y-4">
                <div className="flex justify-between">
                  <span className="text-gray-700">Average ROI</span>
                  <span className="font-bold text-green-600">23%</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-700">Occupancy Rate</span>
                  <span className="font-bold">94%</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-700">Avg Nightly Rate</span>
                  <span className="font-bold">$265</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-700">Payback Period</span>
                  <span className="font-bold">17 months</span>
                </div>
              </div>
            </div>
            
            <div className="bg-white rounded-lg p-8 shadow-lg">
              <div className="text-center mb-6">
                <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
                  <i className="ri-home-line text-2xl text-green-600"></i>
                </div>
                <h3 className="text-2xl font-bold text-gray-900">Tiny Home Communities</h3>
              </div>
              <div className="space-y-4">
                <div className="flex justify-between">
                  <span className="text-gray-700">Average ROI</span>
                  <span className="font-bold text-green-600">19%</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-700">Occupancy Rate</span>
                  <span className="font-bold">92%</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-700">Avg Monthly Rate</span>
                  <span className="font-bold">$1,850</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-700">Payback Period</span>
                  <span className="font-bold">21 months</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Historical Performance */}
      <section className="py-20 px-6">
        <div className="max-w-6xl mx-auto">
          <h2 className="text-4xl font-bold text-gray-900 text-center mb-16">5-Year Performance History</h2>
          
          <div className="grid md:grid-cols-2 gap-12">
            <div className="bg-gray-50 rounded-lg p-8">
              <h3 className="text-2xl font-bold text-gray-900 mb-6">Annual Returns by Year</h3>
              <div className="space-y-4">
                <div className="flex justify-between items-center">
                  <span className="text-gray-700">2019</span>
                  <div className="flex items-center">
                    <div className="w-32 bg-gray-200 rounded-full h-3 mr-3">
                      <div className="bg-green-600 h-3 rounded-full" style={{width: '85%'}}></div>
                    </div>
                    <span className="font-semibold">21%</span>
                  </div>
                </div>
                <div className="flex justify-between items-center">
                  <span className="text-gray-700">2020</span>
                  <div className="flex items-center">
                    <div className="w-32 bg-gray-200 rounded-full h-3 mr-3">
                      <div className="bg-green-600 h-3 rounded-full" style={{width: '72%'}}></div>
                    </div>
                    <span className="font-semibold">18%</span>
                  </div>
                </div>
                <div className="flex justify-between items-center">
                  <span className="text-gray-700">2021</span>
                  <div className="flex items-center">
                    <div className="w-32 bg-gray-200 rounded-full h-3 mr-3">
                      <div className="bg-green-600 h-3 rounded-full" style={{width: '96%'}}></div>
                    </div>
                    <span className="font-semibold">24%</span>
                  </div>
                </div>
                <div className="flex justify-between items-center">
                  <span className="text-gray-700">2022</span>
                  <div className="flex items-center">
                    <div className="w-32 bg-gray-200 rounded-full h-3 mr-3">
                      <div className="bg-green-600 h-3 rounded-full" style={{width: '100%'}}></div>
                    </div>
                    <span className="font-semibold">25%</span>
                  </div>
                </div>
                <div className="flex justify-between items-center">
                  <span className="text-gray-700">2023</span>
                  <div className="flex items-center">
                    <div className="w-32 bg-gray-200 rounded-full h-3 mr-3">
                      <div className="bg-green-600 h-3 rounded-full" style={{width: '88%'}}></div>
                    </div>
                    <span className="font-semibold">22%</span>
                  </div>
                </div>
              </div>
            </div>
            
            <div className="bg-gray-50 rounded-lg p-8">
              <h3 className="text-2xl font-bold text-gray-900 mb-6">Market Comparison</h3>
              <div className="space-y-6">
                <div>
                  <div className="flex justify-between mb-2">
                    <span className="text-gray-700">OED Investments</span>
                    <span className="font-bold text-green-600">22%</span>
                  </div>
                  <div className="w-full bg-gray-200 rounded-full h-3">
                    <div className="bg-green-600 h-3 rounded-full" style={{width: '88%'}}></div>
                  </div>
                </div>
                <div>
                  <div className="flex justify-between mb-2">
                    <span className="text-gray-700">Traditional Real Estate</span>
                    <span className="font-bold">8%</span>
                  </div>
                  <div className="w-full bg-gray-200 rounded-full h-3">
                    <div className="bg-gray-400 h-3 rounded-full" style={{width: '32%'}}></div>
                  </div>
                </div>
                <div>
                  <div className="flex justify-between mb-2">
                    <span className="text-gray-700">Stock Market (S&P 500)</span>
                    <span className="font-bold">10%</span>
                  </div>
                  <div className="w-full bg-gray-200 rounded-full h-3">
                    <div className="bg-gray-400 h-3 rounded-full" style={{width: '40%'}}></div>
                  </div>
                </div>
                <div>
                  <div className="flex justify-between mb-2">
                    <span className="text-gray-700">REITs</span>
                    <span className="font-bold">6%</span>
                  </div>
                  <div className="w-full bg-gray-200 rounded-full h-3">
                    <div className="bg-gray-400 h-3 rounded-full" style={{width: '24%'}}></div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Revenue Streams */}
      <section className="py-20 bg-gray-50 px-6">
        <div className="max-w-6xl mx-auto">
          <h2 className="text-4xl font-bold text-gray-900 text-center mb-16">Multiple Revenue Streams</h2>
          
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8">
            <div className="text-center">
              <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <i className="ri-calendar-line text-2xl text-green-600"></i>
              </div>
              <h3 className="text-xl font-bold text-gray-900 mb-3">Nightly Rentals</h3>
              <p className="text-gray-700 text-sm">
                Premium short-term vacation rentals with high nightly rates and strong seasonal demand
              </p>
            </div>
            
            <div className="text-center">
              <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <i className="ri-home-line text-2xl text-green-600"></i>
              </div>
              <h3 className="text-xl font-bold text-gray-900 mb-3">Long-term Stays</h3>
              <p className="text-gray-700 text-sm">
                Extended stays and monthly rentals providing stable, consistent income streams
              </p>
            </div>
            
            <div className="text-center">
              <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <i className="ri-group-line text-2xl text-green-600"></i>
              </div>
              <h3 className="text-xl font-bold text-gray-900 mb-3">Corporate Retreats</h3>
              <p className="text-gray-700 text-sm">
                High-value corporate bookings for team building and executive retreats
              </p>
            </div>
            
            <div className="text-center">
              <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <i className="ri-heart-line text-2xl text-green-600"></i>
              </div>
              <h3 className="text-xl font-bold text-gray-900 mb-3">Special Events</h3>
              <p className="text-gray-700 text-sm">
                Weddings, celebrations, and special events generating premium revenue
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Investment Calculator */}
      <section className="py-20 px-6">
        <div className="max-w-4xl mx-auto">
          <div className="text-center mb-12">
            <h2 className="text-4xl font-bold text-gray-900 mb-6">Investment Calculator</h2>
            <p className="text-xl text-gray-600">
              See potential returns based on different investment amounts
            </p>
          </div>
          
          <div className="bg-gray-50 rounded-lg p-8">
            <div className="grid md:grid-cols-3 gap-8">
              <div className="text-center">
                <div className="text-2xl font-bold text-gray-900 mb-2">$100,000 Investment</div>
                <div className="text-lg text-gray-600 mb-4">Conservative 18% ROI</div>
                <div className="space-y-2">
                  <div className="flex justify-between">
                    <span>Annual Return:</span>
                    <span className="font-bold text-green-600">$18,000</span>
                  </div>
                  <div className="flex justify-between">
                    <span>Monthly Income:</span>
                    <span className="font-bold">$1,500</span>
                  </div>
                  <div className="flex justify-between">
                    <span>5-Year Total:</span>
                    <span className="font-bold">$190,000</span>
                  </div>
                </div>
              </div>
              
              <div className="text-center">
                <div className="text-2xl font-bold text-gray-900 mb-2">$250,000 Investment</div>
                <div className="text-lg text-gray-600 mb-4">Average 22% ROI</div>
                <div className="space-y-2">
                  <div className="flex justify-between">
                    <span>Annual Return:</span>
                    <span className="font-bold text-green-600">$55,000</span>
                  </div>
                  <div className="flex justify-between">
                    <span>Monthly Income:</span>
                    <span className="font-bold">$4,583</span>
                  </div>
                  <div className="flex justify-between">
                    <span>5-Year Total:</span>
                    <span className="font-bold">$525,000</span>
                  </div>
                </div>
              </div>
              
              <div className="text-center">
                <div className="text-2xl font-bold text-gray-900 mb-2">$500,000 Investment</div>
                <div className="text-lg text-gray-600 mb-4">Premium 25% ROI</div>
                <div className="space-y-2">
                  <div className="flex justify-between">
                    <span>Annual Return:</span>
                    <span className="font-bold text-green-600">$125,000</span>
                  </div>
                  <div className="flex justify-between">
                    <span>Monthly Income:</span>
                    <span className="font-bold">$10,417</span>
                  </div>
                  <div className="flex justify-between">
                    <span>5-Year Total:</span>
                    <span className="font-bold">$1,125,000</span>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Call to Action */}
      <section className="py-20 bg-green-600 px-6">
        <div className="max-w-4xl mx-auto text-center">
          <h2 className="text-4xl font-bold text-white mb-6">Start Earning Superior Returns</h2>
          <p className="text-xl text-green-100 mb-8 leading-relaxed">
            Join our investors who are already earning 15-25% annual returns with our proven glamping and tiny home developments
          </p>
          <Link
            to="/investment-packages"
            className="bg-white text-green-600 hover:bg-gray-100 px-8 py-4 rounded-full text-lg font-semibold transition-colors cursor-pointer whitespace-nowrap"
          >
            Calculate Your Returns
          </Link>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default ReturnsPage;
